import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import './MyAreaReport.css';
import { useTodaysCollectedForArea, calcTodayCollected } from '../utils/useTodaysCollected';

function MyAreaReport({ userArea, areaWiseData }) {
  const captureRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  // Fetch yesterday's per-plaza baseline for this user's area (all areas supported)
  const { data: yesterdayPlazaData, loading: loadingYesterday } = useTodaysCollectedForArea(userArea);

  // Filter data for user's area (no subtotals, actual plaza rows only)
  const myAreaData = areaWiseData.filter(
    row =>
      row.Area &&
      row.Area.toLowerCase().includes(userArea.toLowerCase()) &&
      !row.isSubtotal &&
      !row.isGrandTotal &&
      row.Plaza
  );

  if (myAreaData.length === 0) {
    return (
      <div className="my-area-report">
        <div className="my-area-header">
          <h2>📍 My Area Report: {userArea}</h2>
        </div>
        <div className="no-data-message">
          <p>No data found for your area in the uploaded file.</p>
        </div>
      </div>
    );
  }

  // ── Compute area totals ──────────────────────────────────────────────────
  let totalCollectibleQty  = 0;
  let totalCollectedQty    = 0;
  let totalCollectibleAmt  = 0;
  let totalCollectedAmt    = 0;
  let totalPrevOverdue     = 0;
  let totalRunOverdue      = 0;
  let totalTodayCollected  = 0;

  // Whether any yesterday data exists for this area
  const hasYesterdayData = Object.keys(yesterdayPlazaData).length > 0;

  myAreaData.forEach(row => {
    totalCollectibleQty += parseFloat(row.Collectible_Acc_Qty || 0);
    totalCollectedQty   += parseFloat(row.Collected_Acc_Qty   || 0);
    totalCollectibleAmt += parseFloat(row.Collectible_Amount  || 0);
    totalCollectedAmt   += parseFloat(row.Collected_Amount    || 0);
    totalPrevOverdue    += parseFloat(row.Previous_Overdue    || 0);
    totalRunOverdue     += parseFloat(row.Running_Overdue     || 0);

    const yesterdayQty = yesterdayPlazaData[row.Plaza] || 0;
    totalTodayCollected += calcTodayCollected(row.Collected_Acc_Qty, yesterdayQty);
  });

  const qtyPercent = totalCollectibleQty > 0
    ? ((totalCollectedQty / totalCollectibleQty) * 100).toFixed(2)
    : '0.00';
  const amtPercent = totalCollectibleAmt > 0
    ? ((totalCollectedAmt / totalCollectibleAmt) * 100).toFixed(2)
    : '0.00';
  const overdueChange = totalRunOverdue - totalPrevOverdue;

  const formatNumber = (num) =>
    new Intl.NumberFormat('en-IN').format(Math.round(num));

  // ── html2canvas capture ──────────────────────────────────────────────────
  const generateCanvas = async () => {
    const node = captureRef.current;
    const tableContainer = node.querySelector('.my-area-table-container');
    const table          = node.querySelector('.my-area-table');
    const tableWidth = Math.ceil(
      table
        ? table.scrollWidth
        : tableContainer
        ? tableContainer.scrollWidth
        : node.scrollWidth
    );
    const style    = window.getComputedStyle(node);
    const padLeft  = parseFloat(style.paddingLeft)  || 0;
    const padRight = parseFloat(style.paddingRight) || 0;
    const fullWidth = Math.ceil(tableWidth + padLeft + padRight);

    return html2canvas(node, {
      backgroundColor: '#764ba2',
      scale: 2,
      useCORS: true,
      logging: false,
      width: fullWidth,
      windowWidth: fullWidth,
      scrollX: 0,
      scrollY: 0,
      onclone: (clonedDoc) => {
        const clonedCapture = clonedDoc.querySelector('.my-area-capture');
        if (clonedCapture) {
          clonedCapture.style.width      = fullWidth + 'px';
          clonedCapture.style.maxWidth   = 'none';
          clonedCapture.style.boxSizing  = 'border-box';
        }
        const clonedContainer = clonedDoc.querySelector('.my-area-table-container');
        if (clonedContainer) {
          clonedContainer.style.overflow    = 'visible';
          clonedContainer.style.overflowX   = 'visible';
          clonedContainer.style.maxWidth    = 'none';
          clonedContainer.style.width       = tableWidth + 'px';
        }
        const clonedTable = clonedDoc.querySelector('.my-area-table');
        if (clonedTable) {
          clonedTable.style.width    = tableWidth + 'px';
          clonedTable.style.minWidth = tableWidth + 'px';
        }
      },
    });
  };

  const getFileName = () => {
    const date     = new Date().toISOString().split('T')[0];
    const safeArea = userArea.replace(/[^a-zA-Z0-9]/g, '_');
    return `My_Area_Report_${safeArea}_${date}.png`;
  };

  const handleShareImage = async () => {
    if (!captureRef.current) return;
    setSharing(true);
    try {
      const canvas   = await generateCanvas();
      const blob     = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const fileName = getFileName();
      const file     = new File([blob], fileName, { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `My Area Report: ${userArea}`,
          text:  `📍 My Area Report: ${userArea}`,
        });
      } else {
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href  = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing image:', error);
        alert('❌ Could not share the image. Please try the Download button instead.');
      }
    } finally {
      setSharing(false);
    }
  };

  const handleDownloadImage = async () => {
    if (!captureRef.current) return;
    setSharing(true);
    try {
      const canvas = await generateCanvas();
      const url    = canvas.toDataURL('image/png');
      const link   = document.createElement('a');
      link.href     = url;
      link.download = getFileName();
      link.click();
    } catch (error) {
      console.error('Error downloading image:', error);
      alert('❌ Could not generate the image. Please try again.');
    } finally {
      setSharing(false);
    }
  };

  // Badge class for today's collected value
  const todayBadgeClass = (val) => {
    if (val < 0)   return 'today-collected-badge collected-down';
    if (val < 10)  return 'today-collected-badge collected-warn';
    return 'today-collected-badge collected-up';
  };

  return (
    <div className="my-area-report">
      <div className="my-area-header">
        <h2>📍 My Area Report: {userArea}</h2>
        <div className="my-area-actions">
          <button className="share-btn" onClick={handleShareImage} disabled={sharing}>
            <span>📤</span> {sharing ? 'Preparing...' : 'Share as Image'}
          </button>
          <button className="download-img-btn" onClick={handleDownloadImage} disabled={sharing}>
            <span>⬇️</span> Download
          </button>
        </div>
      </div>

      <div className="my-area-capture" ref={captureRef}>
        <div className="capture-title">
          📍 My Area Report: {userArea}
          <span className="capture-date">{new Date().toLocaleDateString('en-GB')}</span>
        </div>

        {/* Summary stat boxes */}
        <div className="my-area-stats">
          <div className="stat-box">
            <div className="stat-box-label">Total Plazas</div>
            <div className="stat-box-value">{myAreaData.length}</div>
          </div>
          <div className="stat-box">
            <div className="stat-box-label">Card Collected</div>
            <div className="stat-box-value">{formatNumber(totalCollectedQty)}</div>
          </div>
          {/* Today's Collected summary stat — shown for all areas once data loads */}
          {hasYesterdayData && (
            <div className="stat-box">
              <div className="stat-box-label">Today's Collected</div>
              <div className={`stat-box-value ${totalTodayCollected >= 0 ? 'value-up' : 'value-down'}`}>
                {totalTodayCollected > 0 ? '+' : ''}{formatNumber(totalTodayCollected)}
              </div>
            </div>
          )}
          <div className="stat-box">
            <div className="stat-box-label">Qty Achieved</div>
            <div className="stat-box-value">{qtyPercent}%</div>
          </div>
          <div className="stat-box">
            <div className="stat-box-label">Amt Achieved</div>
            <div className="stat-box-value">{amtPercent}%</div>
          </div>
          <div className="stat-box">
            <div className="stat-box-label">O/D Change</div>
            <div className={`stat-box-value ${overdueChange > 0 ? 'value-up' : 'value-down'}`}>
              {overdueChange > 0 ? '▲' : '▼'} {formatNumber(Math.abs(overdueChange))}
            </div>
          </div>
        </div>

        <div className="my-area-table-container">
          <table className="my-area-table">
            <thead>
              <tr>
                <th>Division</th>
                <th>Area</th>
                <th>Plaza</th>
                <th>Target Qty</th>
                <th>Ach. Qty</th>
                <th>Qty %</th>
                {/* Today's Collected column — shown for all users */}
                <th className="today-collected-header">
                  {loadingYesterday ? '⏳ Today' : "Today's Collected"}
                </th>
                <th>Target Amt</th>
                <th>Ach. Amt</th>
                <th>Amt %</th>
                <th>Prev O/D</th>
                <th>Curr O/D</th>
                <th>Inc/Dec</th>
              </tr>
            </thead>
            <tbody>
              {myAreaData.map((plaza, index) => {
                const plazaOverdueChange = parseFloat(plaza.Running_Overdue || 0) - parseFloat(plaza.Previous_Overdue || 0);
                const yesterdayQty       = yesterdayPlazaData[plaza.Plaza] || 0;
                const todayCollected     = calcTodayCollected(plaza.Collected_Acc_Qty, yesterdayQty);
                const noYesterdayRecord  = !yesterdayPlazaData[plaza.Plaza];

                return (
                  <tr key={index}>
                    <td>{plaza.Division}</td>
                    <td>{plaza.Area}</td>
                    <td className="plaza-name-cell">{plaza.Plaza}</td>
                    <td className="number-cell">{formatNumber(plaza.Collectible_Acc_Qty)}</td>
                    <td className="number-cell">{formatNumber(plaza.Collected_Acc_Qty)}</td>
                    <td className="number-cell">
                      <span className="percent-badge">{plaza.Collection_Qty_Percent}%</span>
                    </td>
                    <td className="number-cell today-collected-cell-warn">
                      {loadingYesterday ? (
                        <span className="today-collected-badge">—</span>
                      ) : noYesterdayRecord ? (
                        <span className="today-collected-badge collected-new" title="No yesterday baseline yet">N/A</span>
                      ) : (
                        <span className={todayBadgeClass(todayCollected)}>
                          {todayCollected > 0 ? '+' : ''}{formatNumber(todayCollected)}
                        </span>
                      )}
                    </td>
                    <td className="number-cell">{formatNumber(plaza.Collectible_Amount)}</td>
                    <td className="number-cell">{formatNumber(plaza.Collected_Amount)}</td>
                    <td className="number-cell">
                      <span className="percent-badge">{plaza.Collection_Amt_Percent}%</span>
                    </td>
                    <td className="number-cell">{formatNumber(plaza.Previous_Overdue)}</td>
                    <td className="number-cell">{formatNumber(plaza.Running_Overdue)}</td>
                    <td className="number-cell">
                      <span className={`change-badge ${plazaOverdueChange > 0 ? 'change-up' : 'change-down'}`}>
                        {plazaOverdueChange > 0 ? '▲' : '▼'} {formatNumber(Math.abs(plazaOverdueChange))}
                      </span>
                    </td>
                  </tr>
                );
              })}

              {/* Area Subtotal Row */}
              <tr className="subtotal-row">
                <td></td>
                <td colSpan="2" className="subtotal-label">{userArea} — SUBTOTAL</td>
                <td className="number-cell">{formatNumber(totalCollectibleQty)}</td>
                <td className="number-cell">{formatNumber(totalCollectedQty)}</td>
                <td className="number-cell">
                  <span className="percent-badge">{qtyPercent}%</span>
                </td>
                <td className="number-cell today-collected-cell-warn">
                  {loadingYesterday ? (
                    <span className="today-collected-badge">—</span>
                  ) : !hasYesterdayData ? (
                    <span className="today-collected-badge collected-new">N/A</span>
                  ) : (
                    <span className={todayBadgeClass(totalTodayCollected)}>
                      {totalTodayCollected > 0 ? '+' : ''}{formatNumber(totalTodayCollected)}
                    </span>
                  )}
                </td>
                <td className="number-cell">{formatNumber(totalCollectibleAmt)}</td>
                <td className="number-cell">{formatNumber(totalCollectedAmt)}</td>
                <td className="number-cell">
                  <span className="percent-badge">{amtPercent}%</span>
                </td>
                <td className="number-cell">{formatNumber(totalPrevOverdue)}</td>
                <td className="number-cell">{formatNumber(totalRunOverdue)}</td>
                <td className="number-cell">
                  <span className={`change-badge ${overdueChange > 0 ? 'change-up' : 'change-down'}`}>
                    {overdueChange > 0 ? '▲' : '▼'} {formatNumber(Math.abs(overdueChange))}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="capture-footer">Generated by Collection Analytics By Reza</div>
      </div>
    </div>
  );
}

export default MyAreaReport;
